import React, { useId } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperControls from '../SwiperControls/SwiperControls';
import type { BannersCarouselSkeletonProps, CarouselStyle } from './BannersCarousel.types';

const BannersCarouselSkeleton = ({
    spaceBetweenMobile = 0,
    spaceBetweenTablet = 0,
    spaceBetweenDesktop = 0,
    slidesPerViewMobile = 1,
    slidesPerViewTablet = 1,
    slidesPerViewDesktop = 1,
    heading = { content: '' },
    fullWidth = false,
    bannerHeight,
    showNavigationMobile = false,
    showNavigationDesktop = false,
    showPaginationMobile = false,
    showPaginationDesktop = false,
    navigationPosition = 'below',
    navigationSidesPosition = 'inside',
    paginationPosition = 'below',
    paginationVariant = 'dark',
    className,
    imageFit = 'cover',
}: BannersCarouselSkeletonProps) => {
    const reactId = useId();
    const uniqueId = `banners-carousel-skeleton-${reactId.replace(/:/g, '')}`;
    const slidesCount = Math.max(1, Math.floor(slidesPerViewDesktop));
    
    const headingClass = [
        'banners-carousel__heading',
        !heading.visibility && 'banners-carousel__heading--hidden',
    ].filter(Boolean).join(' ');
    const containerClass = [
        'banners-carousel__container',
        fullWidth && 'banners-carousel__container--full-width',
    ].filter(Boolean).join(' ');
    const sectionClass = ['banners-carousel', 'is-loading', `banners-carousel--fit-${imageFit}`, className]
        .filter(Boolean)
        .join(' ');
    const swiperWrapperClass = navigationPosition === 'sides' && navigationSidesPosition === 'outside'
        ? 'banners-carousel__swiper-wrapper banners-carousel__swiper-wrapper--outside'
        : 'banners-carousel__swiper-wrapper';
    const carouselStyle = bannerHeight
        ? ({ '--banners-carousel-height': `${bannerHeight}px` } as CarouselStyle)
        : undefined;
    
    const breakpointsSetup = {
        991: {
            slidesPerView: slidesPerViewTablet,
            spaceBetween: spaceBetweenTablet,
        },
        1200: {
            slidesPerView: slidesPerViewDesktop,
            spaceBetween: spaceBetweenDesktop,
        },
    };

    const slides = [...Array(slidesCount)].map((_, index) => (
        <SwiperSlide key={index} className="banners-carousel__slide skeleton"></SwiperSlide>
    ));
    
    const swiper = (
        <Swiper
            className="banners-carousel__swiper"
            navigation={false}
            pagination={false}
            spaceBetween={spaceBetweenMobile}
            slidesPerView={slidesPerViewMobile}
            breakpoints={breakpointsSetup}
        >
            {slides}
        </Swiper>
    );

    const controls = (
        <SwiperControls
          uniqueId={uniqueId}
          className="banners-carousel__navigation"
          showNavigationMobile={showNavigationMobile}
          showNavigationDesktop={showNavigationDesktop}
          showPaginationMobile={showPaginationMobile}
          showPaginationDesktop={showPaginationDesktop}
          navigationPosition={navigationPosition}
          navigationSidesPosition={navigationSidesPosition}
          paginationPosition={paginationPosition}
          paginationVariant={paginationVariant}
          isSkeleton={true}
          skeletonPaginationBulletsCount={3}
        />
      );

    const carouselContent = navigationPosition === 'sides' ? (
        <div className={swiperWrapperClass}>
            <div className="banners-carousel__swiper-container">
                {swiper}
            </div>
            {controls}
        </div>
    ) : (
        <>
            {swiper}
            {controls}
        </>
    );

    return (
        <section className={sectionClass} aria-label="Loading banners" aria-hidden="true" style={carouselStyle}>
            <div className={containerClass}>
                <h2 className={headingClass}>{heading.content}</h2>
                {carouselContent}
            </div>
        </section>
    );
};

export default BannersCarouselSkeleton;